import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingScreen, EmptyState } from "@/components/common";

const BLANK = { category: "customer", question: "", answer: "", enabled: true };

export default function AdminFAQ() {
  const [faqs, setFaqs] = useState(null);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/faqs").then((r) => setFaqs(r.data));
  useEffect(() => { load().catch(() => toast.error("Failed to load")); }, []);
  if (!faqs) return <DashboardShell menu={adminMenu} title="Manage FAQs"><LoadingScreen /></DashboardShell>;

  const q = query.trim().toLowerCase();
  const filtered = faqs
    .filter((f) => tab === "all" || f.category === tab)
    .filter((f) => !q || f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q));

  const toggle = async (f) => {
    setFaqs(faqs.map((x) => (x.id === f.id ? { ...x, enabled: !f.enabled } : x)));
    try { await api.put(`/admin/faqs/${f.id}`, { enabled: !f.enabled }); toast.success(!f.enabled ? "Published" : "Unpublished"); }
    catch { toast.error("Failed"); load(); }
  };
  const del = async (f) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try { await api.delete(`/admin/faqs/${f.id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed to delete"); }
  };
  const move = async (f, dir) => {
    const sameList = faqs.slice().sort((a, b) => a.order - b.order);
    const idx = sameList.findIndex((x) => x.id === f.id);
    const swap = sameList[idx + dir];
    if (!swap) return;
    const newOrder = [{ id: f.id, order: swap.order }, { id: swap.id, order: f.order }];
    try { await api.post("/admin/faqs/reorder", { order: newOrder }); load(); } catch { toast.error("Failed to reorder"); }
  };
  const save = async () => {
    if (!editing.question.trim() || !editing.answer.trim()) { toast.error("Question and answer are required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/faqs/${editing.id}`, editing);
      else await api.post("/admin/faqs", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  return (
    <DashboardShell menu={adminMenu} title="Manage FAQs">
      <PageHeader title="FAQ Management" subtitle="Add, edit, reorder and publish FAQs. Published FAQs appear on the public website."
        action={<Button className="bg-brand hover:bg-brand-600" onClick={() => setEditing({ ...BLANK })} data-testid="faq-add-btn"><Plus size={16} className="mr-1.5" /> Add FAQ</Button>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="faq-tabs">
            <TabsTrigger value="all" data-testid="faq-tab-all">All</TabsTrigger>
            <TabsTrigger value="customer" data-testid="faq-tab-customer">Customer</TabsTrigger>
            <TabsTrigger value="agency" data-testid="faq-tab-agency">Agency</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" size={16} />
          <Input className="pl-9" placeholder="Search FAQs…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="faq-admin-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No FAQs found" hint="Try a different filter or add a new FAQ." />
      ) : (
        <div className="space-y-3">
          {filtered.map((f, i) => (
            <div key={f.id} className="bg-white border border-navy-200 rounded-xl p-4 flex items-start gap-4" data-testid={`faq-row-${f.id}`}>
              <div className="flex flex-col gap-1 pt-1">
                <button onClick={() => move(f, -1)} disabled={i === 0} className="text-navy-400 hover:text-brand disabled:opacity-30" data-testid={`faq-up-${f.id}`}><ArrowUp size={16} /></button>
                <button onClick={() => move(f, 1)} disabled={i === filtered.length - 1} className="text-navy-400 hover:text-brand disabled:opacity-30" data-testid={`faq-down-${f.id}`}><ArrowDown size={16} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="capitalize border-navy-300 text-navy-600">{f.category}</Badge>
                  {!f.enabled && <Badge className="bg-navy-100 text-navy-500 hover:bg-navy-100">Unpublished</Badge>}
                </div>
                <p className="mt-1.5 font-heading font-semibold text-navy-900">{f.question}</p>
                <p className="mt-1 text-sm text-navy-600 line-clamp-2">{f.answer}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5 mr-1" title="Published">
                  <Switch checked={f.enabled} onCheckedChange={() => toggle(f)} data-testid={`faq-toggle-${f.id}`} />
                </div>
                <Button size="icon" variant="outline" className="border-navy-300 h-9 w-9" onClick={() => setEditing({ ...f })} data-testid={`faq-edit-${f.id}`}><Pencil size={15} /></Button>
                <Button size="icon" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-9 w-9" onClick={() => del(f)} data-testid={`faq-delete-${f.id}`}><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="faq-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit FAQ" : "Add FAQ"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="faq-dialog-category"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="customer">Customer</SelectItem><SelectItem value="agency">Agency</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Question</Label><Input className="mt-1" value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} data-testid="faq-dialog-question" /></div>
              <div><Label className="text-xs">Answer</Label><Textarea className="mt-1" rows={5} value={editing.answer} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} data-testid="faq-dialog-answer" /></div>
              <div className="flex items-center gap-3"><Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} data-testid="faq-dialog-enabled" /><Label className="text-sm">Published (visible on website)</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-navy-300" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-600" disabled={saving} onClick={save} data-testid="faq-dialog-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
